// ---------------------------------------------------------------------------
// Best-effort OS notification for finished background AI tasks.
//
// The in-app toast is always shown by the background-task runner; this adds a
// system notification ONLY when the window is not focused, so a user who
// switched to another app still learns the task finished. Desktop uses the
// Tauri notification plugin (exposed on the __TAURI__ global); the web build
// falls back to the Web Notification API. Every failure is swallowed — a
// missing notification must never break the task itself.
// ---------------------------------------------------------------------------

import { isTauri } from '../ai/tauri';

interface TauriNotificationApi {
  isPermissionGranted: () => Promise<boolean>;
  requestPermission: () => Promise<string>;
  sendNotification: (options: { title: string; body: string }) => void | Promise<void>;
}

export async function notifyOs(title: string, body: string): Promise<void> {
  try {
    // Window in the foreground: the toast is visible, a system popup is noise.
    if (typeof document !== 'undefined' && document.hasFocus()) return;

    if (isTauri()) {
      const api = (window as unknown as { __TAURI__?: { notification?: TauriNotificationApi } })
        .__TAURI__?.notification;
      if (!api?.sendNotification) return;
      let granted = await api.isPermissionGranted();
      if (!granted) granted = (await api.requestPermission()) === 'granted';
      if (granted) await api.sendNotification({ title, body });
      return;
    }

    if (typeof Notification !== 'undefined') {
      if (Notification.permission === 'default') await Notification.requestPermission();
      if (Notification.permission === 'granted') new Notification(title, { body });
    }
  } catch {
    // Best-effort only.
  }
}
